import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { classNames } from "../../../lib/classNames";
import { getExtension, getFileName } from "../utils";
import { FileUploadProgress } from "./FileUploadProgress";
import { FileContent } from "./FileContent";
import { useAppSelector } from "../../../../shared/hooks/useAppSelector";
import { isUploadFileProgressSelector } from "../../../model";
import type { ChangeEvent, InputHTMLAttributes, ForwardedRef, DragEvent } from "react";

import styles from "./FileUpload.module.scss";

interface FileUploadProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
    /** @note функция записи объекта File */
    onFileChange: (file?: File) => void;
    /** @note объект File */
    data?: File;
    /** @note краткое описание файла */
    description?: string;
    /** @note Класс */
    className?: string;
    /** @deprecated для совместимости */
    errorMessage?: string;
    /** @deprecated для совместимости */
    isLoading?: boolean;
    /** @deprecated для совместимости */
    isError?: boolean;
    /** @deprecated для совместимости */
    isSuccess?: boolean;
}

export const FileUpload = forwardRef(
    (
        { data, description = "резюме", onFileChange, accept, className, ...props }: FileUploadProps,
        ref: ForwardedRef<HTMLInputElement | null>,
    ) => {
        const isLoading = useAppSelector(isUploadFileProgressSelector);

        const [fileInputKey, setFileInputKey] = useState(0);
        const [isDragActive, setIsDragActive] = useState(false);
        const [isError, setIsError] = useState(false);

        const innerInputFileRef = useRef<HTMLInputElement | null>(null);

        useImperativeHandle(ref, () => innerInputFileRef.current as HTMLInputElement);

        const fileName = getFileName(data?.name) ?? "";
        const extension = getExtension(data?.name, fileName);
        const formats = accept?.replaceAll(".", "");

        const changeFile = (file?: File) => {
            setIsError(false);

            const fileName = getFileName(file?.name) ?? "";
            const extension = getExtension(file?.name, fileName);

            if (accept && extension) {
                const isAccept = accept?.split(",").some((item) => item.includes(extension));

                if (!isAccept) {
                    setIsError(true);
                    onFileChange();
                    return;
                }
            }

            onFileChange(file);
            setFileInputKey((prevKey) => prevKey + 1);
        };

        const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
            changeFile(event.target.files?.[0]);
        };

        const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragActive(true);
        };

        const handleDropLeave = (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragActive(false);
        };

        const hadleDrop = (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            changeFile(event.dataTransfer.files[0]);
            setIsDragActive(false);
        };

        const hanleClick = () => innerInputFileRef.current?.click();

        return (
            <div className={styles.wrapper}>
                <input
                    key={fileInputKey}
                    type="file"
                    onChange={handleFileChange}
                    ref={innerInputFileRef}
                    accept={accept}
                    className={styles.input}
                    {...props}
                />
                <div
                    className={classNames({
                        cls: fileName ? styles.file : styles.border,
                        mods: {
                            [styles.active]: isDragActive,
                            [styles.error]: isError,
                        },
                        additional: [className],
                    })}
                    {...(isLoading
                        ? {}
                        : {
                              onDragStart: handleDragStart,
                              onDragOver: handleDragStart,
                              onDragLeave: handleDropLeave,
                              ...(isDragActive ? { onDrop: hadleDrop } : { onClick: hanleClick }),
                          })}
                >
                    {isLoading ? (
                        <FileUploadProgress>
                            <FileContent isFile extension={extension} content={fileName} />
                        </FileUploadProgress>
                    ) : (
                        <FileContent
                            content={
                                isError
                                    ? `Ошибка формата. Верный ${formats}.`
                                    : fileName || `Прикрепить ${description} ${formats}`
                            }
                            {...(Boolean(fileName)
                                ? { onClear: onFileChange, extension, isFile: true }
                                : { isFile: false })}
                        />
                    )}
                </div>
            </div>
        );
    },
);
